import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Instagram, Mail, Phone, ArrowUpRight } from "lucide-react";
import { LOGO_URL } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("You're on the list.", { description: "Expect editorial drops, not spam." });
      setEmail("");
    } catch {
      toast.error("Could not subscribe. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="relative mt-32 border-t border-white/5 bg-[#050505]" data-testid="site-footer">
      <div className="max-w-[1480px] mx-auto px-6 md:px-10 py-20 grid grid-cols-1 md:grid-cols-12 gap-12">
        {/* Brand block */}
        <div className="md:col-span-5">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="" className="h-12 w-12 object-contain" />
            <div>
              <div className="font-display text-3xl text-white">LUCRA</div>
              <div className="font-mono-tech text-[10px] text-white/40">PHOTOGRAPHY · EST. ATL / DC</div>
            </div>
          </Link>
          <p className="mt-6 text-white/55 max-w-md leading-relaxed">
            Cinematic photography &amp; motion for weddings, brands, and the people who refuse to look like everyone else.
          </p>

          <form onSubmit={subscribe} className="mt-8 flex items-center gap-3 max-w-md" data-testid="newsletter-form">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@yourdomain.com"
              className="lucra-input"
              data-testid="newsletter-input"
            />
            <button
              type="submit"
              disabled={loading}
              className="btn-gold whitespace-nowrap"
              data-testid="newsletter-submit"
            >
              <span>{loading ? "..." : "Subscribe"}</span>
            </button>
          </form>
        </div>

        <div className="md:col-span-3">
          <div className="lucra-label">Navigate</div>
          <ul className="mt-5 space-y-3 font-display text-lg">
            {["About","Services","Clients","Portfolio","Pricing","Journal","Careers","Contact"].map((n) => (
              <li key={n}>
                <Link
                  to={`/${n.toLowerCase() === "journal" ? "blog" : n.toLowerCase()}`}
                  className="text-white/70 hover:text-[var(--gold)] inline-flex items-center gap-2 transition-colors group"
                >
                  {n}
                  <ArrowUpRight size={14} className="opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition" />
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-4">
          <div className="lucra-label">Studios</div>
          <div className="mt-5 space-y-5">
            <div>
              <div className="font-display text-lg text-white">Atlanta</div>
              <div className="text-white/55 text-sm mt-1">Westside Provisions District<br />Atlanta, GA · By appointment</div>
            </div>
            <div>
              <div className="font-display text-lg text-white">Washington, D.C.</div>
              <div className="text-white/55 text-sm mt-1">Union Market District<br />Washington, DC · By appointment</div>
            </div>
          </div>

          <div className="mt-8 space-y-2 font-mono-tech text-xs text-white/60">
            <a href="mailto:info@lucraphoto.com" className="flex items-center gap-2 hover:text-[var(--gold)]"><Mail size={14} />info@lucraphoto.com</a>
            <a href="tel:+14705551427" className="flex items-center gap-2 hover:text-[var(--gold)]"><Phone size={14} />+1 (470) 555-1427</a>
            <a href="https://instagram.com" target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-[var(--gold)]"><Instagram size={14} />@lucra.photo</a>
          </div>
        </div>
      </div>

      <div className="border-t border-white/5">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-3 font-mono-tech text-[10px] tracking-[0.2em] uppercase text-white/35">
          <div>© {new Date().getFullYear()} Lucra Photography · All Rights Reserved</div>
          <div>Frame No. 0001 · Atlanta / DC</div>
          <div>
            Website &amp; SEO by{" "}
            <a
              href="https://webnehtech.com"
              target="_blank"
              rel="noreferrer"
              className="text-white/60 hover:text-[var(--gold)] transition-colors"
              data-testid="footer-credit-link"
            >
              webnehtech.com
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
